import React, { type FC } from "react";
import MytripsLoginModalContainer from "@components/AuthFlow/AuthenticationModal/MytripsLoginModalContainer";
import Footer from "@components/AuthFlow/Footer";
import PasswordlessForm from "@components/AuthFlow/Passwordless/PasswordlessForm";
import SigninForm from "@components/AuthFlow/Signin/SigninForm";
import ScreenProvider, {
  screenNames,
} from "clientState/AuthFlow/providers/ScreenProvider";
import { useTranslation } from "@edreamsodigeo/frontend-core";
import { Box } from "@edreamsodigeo/prisma-design-system";

import { MYTRIPS_LOGIN_GA_TRACKING_PREFIX } from "../MyTripsLogin/config/tracking";
import type { MytripsLoginAuthFlowProps } from "../MyTripsLogin/contract";
import useMytripsLoginFlow from "../useMyTripsLoginAuthFlow";
import IncentivizeMytripsLoginButtons from "./IncentivizeMyTripsLoginButtons";
import IncentivizeMytripsLoginHeader from "./IncentivizeMyTripsLoginHeader";

const { SIGNIN } = screenNames;

const IncentivizeMyTripsLoginComponent: FC<MytripsLoginAuthFlowProps> = ({
  successRedirectionUrl,
}) => {
  const { t } = useTranslation();
  const {
    mytripsLoginFlow,
    email,
    setEmail,
    acceptsNewsletter,
    onRenderPasswordless,
    onRenderSuccessScreen,
    onRenderLoginSyncScreen,
    openForgotPasswordFlow,
    onSuccessNavigation,
    onClose,
    onRenderSignin,
  } = useMytripsLoginFlow(successRedirectionUrl);
  const SCREEN_WITH_PASSWORD_CLICK_ID = `${mytripsLoginFlow}_click:with-password`;

  return (
    <Box pt={{ _: "base" }}>
      <IncentivizeMytripsLoginHeader
        mytripsLoginFlow={mytripsLoginFlow}
        onRenderPasswordless={onRenderPasswordless}
      />
      {mytripsLoginFlow === SIGNIN ? (
        <SigninForm
          email={email}
          setEmail={setEmail}
          onForgotPasswordClick={openForgotPasswordFlow}
          closeScreen={onSuccessNavigation}
        />
      ) : (
        <PasswordlessForm
          email={email}
          setEmail={setEmail}
          acceptsNewsletter={acceptsNewsletter}
          submitButtonLabel={t("authentication.edo.passwordless.submit")}
          emailLabelForm={t("authentication.auth.form.email.label")}
          isLoginSyncEnabled={false}
          renderSuccessScreen={onRenderSuccessScreen}
          renderLoginSyncScreen={onRenderLoginSyncScreen}
        />
      )}
      <IncentivizeMytripsLoginButtons
        signinLink={{
          trackingId: SCREEN_WITH_PASSWORD_CLICK_ID,
          content:
            mytripsLoginFlow === SIGNIN
              ? t("authentication.signin.passwordless")
              : t("authentication.edo.passwordless.withPasswordLabel"),
        }}
        showSigninLink
        renderSigninScreen={onRenderSignin}
        renderPasswordlessScreen={onRenderPasswordless}
        mytripsLoginFlow={mytripsLoginFlow}
        googleButtonScreen={mytripsLoginFlow}
      />
      <MytripsLoginModalContainer closeModal={onClose} />
      <Footer withGeneralTC />
    </Box>
  );
};

const IncentivizeMyTripsLogin: FC<MytripsLoginAuthFlowProps> = (props) => (
  <ScreenProvider gaTrackingPrefix={MYTRIPS_LOGIN_GA_TRACKING_PREFIX}>
    <IncentivizeMyTripsLoginComponent {...props} />
  </ScreenProvider>
);

export default IncentivizeMyTripsLogin;
